import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import NlimsDataTable from '../../component/shared/NlimsDataTable/NlimsDataTable';
import UnconfiguredSubmodulePlaceholder from '../../component/shared/UnconfiguredSubmodulePlaceholder';
import { useModuleTabs } from '../../config/moduleCatalog';
import {
  animalRegistrationsApi,
  loadAnimalRegistrationLookups,
  normalizeAnimalRegistration,
  normalizeDateValue,
  EMPTY_LOOKUPS,
} from '../../api/animalRegistrationsApi';

const LOCAL_TABS = [{ key: 'registrations', label: 'Animal ID Registrations' }];

const TAG_STATUS_STYLE = {
  active: 'background:#dcfce7;color:#15803d;border:1px solid #86efac;',
  lost: 'background:#fee2e2;color:#dc2626;border:1px solid #fca5a5;',
  damaged: 'background:#fff7ed;color:#c2410c;border:1px solid #fed7aa;',
  replaced: 'background:#eff6ff;color:#1d4ed8;border:1px solid #93c5fd;',
};

function normalizeKey(value) {
  return String(value ?? '')
    .trim()
    .toLowerCase()
    .replace(/[\s_-]+/g, '_');
}

function humanizeLabel(value) {
  const text = String(value ?? '').trim();
  if (!text) return '';
  return text
    .replace(/[_-]+/g, ' ')
    .replace(/\s+/g, ' ')
    .replace(/\b\w/g, (char) => char.toUpperCase());
}

function tagStatusHtml(data, type) {
  if (type !== 'display') return data ?? '';
  const key = normalizeKey(data);
  const style = TAG_STATUS_STYLE[key] || 'background:#f3f4f6;color:#6b7280;';
  const label = humanizeLabel(data) || '—';
  return `<span style="display:inline-flex;padding:2px 10px;border-radius:6px;font-size:11px;font-weight:600;${style}">${label}</span>`;
}

function parseError(error, fallback) {
  return (
    error?.response?.data?.message ||
    error?.response?.data?.error ||
    error?.message ||
    fallback
  );
}

function getRowDate(row = {}) {
  return normalizeDateValue(
    row.registrationDate ??
    row.registration_date ??
    row.created_at ??
    row.createdAt ??
    row.updated_at ??
    row.updatedAt
  );
}

function isWithinRange(date, fromDate, toDate) {
  if (!date) return true;

  if (fromDate) {
    const start = normalizeDateValue(fromDate);
    if (start && date < start) return false;
  }

  if (toDate) {
    const end = normalizeDateValue(toDate);
    if (end) {
      end.setHours(23, 59, 59, 999);
      if (date > end) return false;
    }
  }

  return true;
}

const COLUMNS = [
  { title: 'Animal ID', data: 'tagId', sortable: true },
  { title: 'Tag Type', data: 'tagType', sortable: true },
  { title: 'Tag Status', data: 'tagStatus', sortable: true, render: tagStatusHtml },
  { title: 'Species', data: 'species', sortable: true },
  { title: 'Breed', data: 'breed', sortable: true },
  { title: 'Sex', data: 'sex', sortable: true },
  { title: 'Age Class', data: 'ageClass', sortable: true },
  { title: 'Herd/Holding Reference', data: 'herdHoldingRef', sortable: true },
  { title: 'Owner', data: 'owner', sortable: true },
  { title: 'State', data: 'state', sortable: true },
  { title: 'LGA', data: 'lga', sortable: true },
];

export default function AnimalIdentification() {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const tabs = useModuleTabs('animal-identification', LOCAL_TABS);
  const segment = pathname.split('/').filter(Boolean).pop();
  const activeTab = tabs.some((tab) => tab.key === segment) ? segment : tabs[0]?.key;
  const activeTabDef = tabs.find((tab) => tab.key === activeTab);

  const [records, setRecords] = useState([]);
  const [search, setSearch] = useState('');
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [refreshTick, setRefreshTick] = useState(0);

  useEffect(() => {
    let active = true;

    const load = async () => {
      setLoading(true);
      setError('');

      const [recordsResult, lookupsResult] = await Promise.allSettled([
        animalRegistrationsApi.listAll({ per_page: 100 }),
        loadAnimalRegistrationLookups(),
      ]);

      if (!active) return;

      const lookups = lookupsResult.status === 'fulfilled' ? lookupsResult.value : EMPTY_LOOKUPS;

      if (recordsResult.status === 'fulfilled') {
        const items = recordsResult.value?.items ?? [];
        setRecords(items.map((row) => normalizeAnimalRegistration(row, lookups)));
      } else {
        setRecords([]);
        setError(parseError(recordsResult.reason, 'Failed to load animal registrations.'));
      }

      setLoading(false);
    };

    load();

    return () => {
      active = false;
    };
  }, [refreshTick]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return records.filter((row) => {
      const matchQ =
        !q ||
        Object.values(row).some(
          (value) => typeof value === 'string' && value.toLowerCase().includes(q)
        );
      const matchFrom = !fromDate || isWithinRange(getRowDate(row), fromDate, null);
      const matchTo = !toDate || isWithinRange(getRowDate(row), null, toDate);
      return matchQ && matchFrom && matchTo;
    });
  }, [fromDate, records, search, toDate]);

  return (
    <div className="p-6 bg-white min-h-screen">
      <div className="flex items-start justify-between mb-6">
        <div>
          <h1 className="text-xl font-bold text-gray-900 leading-none">
            Animal Identification, Traceability &amp; Movement
          </h1>
          <button
            type="button"
            className="inline-flex items-center gap-1 text-xs font-semibold text-green-600 underline underline-offset-2 mt-1.5 hover:text-green-700"
          >
            Source from NAITS
            <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
            </svg>
          </button>
        </div>
        <span className="text-xs font-semibold text-blue-700 bg-blue-50 border border-blue-200 px-2.5 py-1 rounded-full mt-1">
          NAITS Integrated
        </span>
      </div>

      <nav className="mb-5 flex flex-wrap gap-x-7 gap-y-2 border-b border-gray-200">
        {tabs.map(({ key, label }) => {
          const isActive = activeTab === key;
          return (
            <button
              key={key}
              onClick={() => navigate(`/animal-identification/${key}`)}
              className={`relative pb-3 text-sm ${
                isActive ? 'font-semibold text-green-600' : 'font-medium text-gray-700 hover:text-green-700'
              }`}
            >
              {label}
              {isActive && <span className="absolute inset-x-0 -bottom-px h-0.5 rounded bg-green-600" />}
            </button>
          );
        })}
      </nav>

      {activeTabDef?.isCatalogOnly ? (
        <UnconfiguredSubmodulePlaceholder label={activeTabDef.label} />
      ) : (
        <>
          {error && (
            <div className="mb-4 flex items-center justify-between gap-3 rounded-2xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
              <span>{error}</span>
              <button
                type="button"
                onClick={() => setRefreshTick((value) => value + 1)}
                className="rounded-lg border border-red-200 bg-white px-3 py-1.5 text-xs font-semibold text-red-700 hover:bg-red-100"
              >
                Retry
              </button>
            </div>
          )}

          <div className="flex items-center justify-between gap-4 mb-5">
            <div className="flex items-center gap-2">
              <div className="relative">
                <svg className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                </svg>
                <input
                  type="text"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Search here"
                  className="pl-9 pr-4 py-2 text-sm border border-gray-200 rounded-lg w-64 text-gray-700 placeholder-gray-400 focus:outline-none focus:border-green-400 focus:ring-1 focus:ring-green-400"
                />
              </div>
              <button
                onClick={() => {}}
                className="px-5 py-2 text-sm font-semibold text-green-700 bg-green-50 border border-green-200 rounded-lg hover:bg-green-100 transition-colors"
              >
                Search
              </button>
            </div>

            <div className="flex items-center gap-2">
              <span className="text-sm text-gray-500 font-medium">From</span>
              <div className="relative">
                <input
                  type="date"
                  value={fromDate}
                  onChange={(e) => setFromDate(e.target.value)}
                  className="px-3 py-2 text-sm border border-gray-200 rounded-lg text-gray-600 focus:outline-none focus:border-green-400 focus:ring-1 focus:ring-green-400 cursor-pointer"
                />
              </div>
              <span className="text-sm text-gray-500 font-medium">To</span>
              <div className="relative">
                <input
                  type="date"
                  value={toDate}
                  onChange={(e) => setToDate(e.target.value)}
                  className="px-3 py-2 text-sm border border-gray-200 rounded-lg text-gray-600 focus:outline-none focus:border-green-400 focus:ring-1 focus:ring-green-400 cursor-pointer"
                />
              </div>
              <button
                onClick={() => {}}
                className="px-5 py-2 text-sm font-semibold text-green-700 bg-green-50 border border-green-200 rounded-lg hover:bg-green-100 transition-colors"
              >
                Search
              </button>
            </div>
          </div>

          <NlimsDataTable
            tableId="animal-identification-table"
            columns={COLUMNS}
            data={filtered}
            serverSide={false}
            hideSearch
            pageLength={10}
            enableRowSelection
          />

          {loading && records.length === 0 && !error && (
            <div className="mt-4 text-sm text-gray-500">Loading animal registrations...</div>
          )}
        </>
      )}
    </div>
  );
}
